import { MessageBarType } from '@fluentui/react';
import { IColumn, IGroup } from '@fluentui/react/lib/DetailsList';
import { DetailsList, IconButton, Item, MessageBar, TooltipHost, useTheme } from '@h2oai/ui-kit';
import { useEffect, useMemo, useState } from 'react';

import { AppEventsRequest, AppEventsResponse } from '../../../../../admin-center/gen/app_event/appEvent/appevent_pb';
import { useAdminCenterService } from '../../../../../admin-center/hooks';
import { useUser } from '../../../../../utils/hooks';
import { formatDefaultDateAndTime } from '../../../../../utils/utils';
import {
  columnHeaderContainerStyles,
  columnStyles,
  customTableStyles,
  groupIconStyle,
  tooltipHostStyles,
} from '../../../common/common.styles';
import { buildUserContext, getDummyList } from '../../../common/utils';
import { AppEventName, AppEventOriginalName, AppEventTableColKey, AppEventTableColName, NA } from '../../../constants';
import SkeletonLoader from '../../SkeletonLoader/SkeletonLoader';
import { tableStyle } from '../AppEvent.styles';
import {
  createdEventStyles,
  deletedEventStyles,
  likedEventStyles,
  pinnedEventStyles,
  updatedEventStyles,
} from './AppTable.styles';

interface AppEvent {
  appName: string;
  appEvent: string;
  appUser: string;
  appDate: string;
  appTime: string;
}

interface AppEventTableProps {
  startDate: string;
  endDate: string;
  searchQuery: string;
}

const dummyList = getDummyList<AppEvent>(8);

export default function AppTable({ startDate, endDate, searchQuery }: AppEventTableProps) {
  const service = useAdminCenterService();
  const theme = useTheme();
  const user = useUser();
  const [appEventData, setAppEventData] = useState<AppEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [groupByKeys, setGroupByKeys] = useState<string[]>([]);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);

  const fetchAppEvents = async () => {
    setIsLoading(true);
    try {
      const userContext = await buildUserContext(user);
      const request: AppEventsRequest = {
        startDate: startDate,
        endDate: endDate,
        userContext: userContext,
      };

      const response: AppEventsResponse = await service.getAppEvents(request);

      const APP_EVENT_MAP: Record<string, string> = {
        [AppEventOriginalName.create]: AppEventName.create,
        [AppEventOriginalName.delete]: AppEventName.delete,
        [AppEventOriginalName.like]: AppEventName.like,
        [AppEventOriginalName.pin]: AppEventName.pin,
        [AppEventOriginalName.update]: AppEventName.update,
        nan: NA,
      };
      const getAppEventColumnEntry = (event: string | undefined): string => {
        if (!event) return NA;
        return APP_EVENT_MAP[event.toLowerCase()] || event;
      };

      const getAppTitleColumnEntry = (event: string | undefined): string => {
        if (!event) return NA;
        try {
          const parsedEvent = JSON.parse(event);
          if (parsedEvent && typeof parsedEvent === 'object' && 'appEvent' in parsedEvent) {
            const events = parsedEvent['appEvent'];
            const firstEventKey = Object.keys(events)[0];

            if (firstEventKey && events[firstEventKey] && events[firstEventKey]['app']) {
              return events[firstEventKey]['app']['title'];
            }
          }
        } catch (e) {
          return NA;
        }
        return NA;
      };

      const formattedData = (response.events ?? []).map((event) => {
        const utcDate = event.ts ? new Date(event.ts) : null;

        return {
          appName: getAppTitleColumnEntry(event.label) ?? '-',
          appEvent: getAppEventColumnEntry(event.stream) ?? '-',
          appUser: event.userName ?? '-',
          appDate: formatDefaultDateAndTime(utcDate) || '-',
          appTime: utcDate
            ? utcDate.toLocaleTimeString('en-US', {
                hour12: false,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              })
            : '-',
        };
      });

      setAppEventData(formattedData);
    } catch (error) {
      console.error('Error fetching login events:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAppEvents();
  }, [startDate, endDate]);

  const filteredData = useMemo(() => {
    const searchTerm = searchQuery.toLowerCase();
    let filtered = appEventData.filter(
      (item) =>
        item.appName.toLowerCase().includes(searchTerm) ||
        item.appEvent.toLowerCase().includes(searchTerm) ||
        item.appUser.toLowerCase().includes(searchTerm) ||
        item.appDate.toLowerCase().includes(searchTerm) ||
        item.appTime.toLowerCase().includes(searchTerm)
    );

    if (sortColumn) {
      filtered = [...filtered].sort((a, b) => {
        const valA = a[sortColumn as keyof AppEvent] as string;
        const valB = b[sortColumn as keyof AppEvent] as string;
        return isSortedDescending ? valA.localeCompare(valB) : valB.localeCompare(valA);
      });
    }

    return filtered;
  }, [appEventData, searchQuery, sortColumn, isSortedDescending]);

  const { groupedItems, groups } = useMemo(() => {
    if (groupByKeys.length === 0) return { groupedItems: filteredData, groups: undefined };

    const groupedMap = new Map<string, { items: AppEvent[]; keys: string[] }>();

    filteredData.forEach((item) => {
      const keyValues = groupByKeys.map((key) => String(item[key as keyof AppEvent]));
      const groupKey = keyValues.join('||');

      if (!groupedMap.has(groupKey)) {
        groupedMap.set(groupKey, { items: [], keys: keyValues });
      }

      groupedMap.get(groupKey)!.items.push(item);
    });

    const groupedItems: AppEvent[] = [];
    const groups: IGroup[] = [];

    let startIndex = 0;
    for (const [groupKey, { items, keys }] of groupedMap) {
      groups.push({
        key: groupKey,
        name: keys.join(' → '),
        startIndex,
        count: items.length,
        level: 0,
      });

      groupedItems.push(...items);
      startIndex += items.length;
    }

    return { groupedItems, groups };
  }, [filteredData, groupByKeys]);

  const handleColumnClick = (_ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
    setSortColumn(column.fieldName);
    if (sortColumn === column.fieldName) {
      setIsSortedDescending(!isSortedDescending);
    } else {
      setIsSortedDescending(false);
    }
  };

  const renderHeader = (colName: string, colKey: string) => {
    const isGrouped = groupByKeys.includes(colKey);
    return (
      <div style={columnHeaderContainerStyles}>
        {colName}
        <TooltipHost styles={tooltipHostStyles} content={`Group by ${colName}`}>
          <IconButton
            iconProps={{ iconName: 'GroupList' }}
            styles={groupIconStyle(isGrouped, theme)}
            onClick={(e) => {
              e.stopPropagation();
              setGroupByKeys((prev) => {
                if (prev.includes(colKey)) {
                  return prev.filter((k) => k !== colKey);
                }
                return prev.length < 2 ? [...prev, colKey] : [colKey];
              });
            }}
          />
        </TooltipHost>
      </div>
    );
  };

  const columns: IColumn[] = [
    {
      key: AppEventTableColKey.name,
      fieldName: AppEventTableColKey.name,
      name: AppEventTableColName.name,
      minWidth: 200,
      onRender: (item: AppEvent) => <>{!isLoading ? item.appName : <SkeletonLoader width="65%" height="100%" />}</>,
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(AppEventTableColName.name, AppEventTableColKey.name),
      isSorted: sortColumn === AppEventTableColKey.name,
      isSortedDescending: sortColumn === AppEventTableColKey.name && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: AppEventTableColKey.event,
      fieldName: AppEventTableColKey.event,
      name: AppEventTableColName.event,
      minWidth: 150,
      onRender: (item: any) => {
        if (isLoading) return <SkeletonLoader width="65%" height="100%" />;
        const event = item.appEvent as AppEventName;
        const eventStyles = {
          [AppEventName.create]: createdEventStyles,
          [AppEventName.delete]: deletedEventStyles,
          [AppEventName.like]: likedEventStyles,
          [AppEventName.pin]: pinnedEventStyles,
          [AppEventName.update]: updatedEventStyles,
        };
        return (
          <div>
            <Item
              data={{
                id: '',
                title: event,
              }}
              styles={eventStyles[event] || {}}
              idField="id"
              labelField="title"
            />
          </div>
        );
      },
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(AppEventTableColName.event, AppEventTableColKey.event),
      isSorted: sortColumn === AppEventTableColKey.event,
      isSortedDescending: sortColumn === AppEventTableColKey.event && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: AppEventTableColKey.user,
      fieldName: AppEventTableColKey.user,
      name: AppEventTableColName.user,
      minWidth: 200,
      onRender: (item: AppEvent) => <>{!isLoading ? item.appUser : <SkeletonLoader width="65%" height="100%" />}</>,
      styles: columnStyles(true),
      onRenderHeader: () => renderHeader(AppEventTableColName.user, AppEventTableColKey.user),
      isSorted: sortColumn === AppEventTableColKey.user,
      isSortedDescending: sortColumn === AppEventTableColKey.user && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: AppEventTableColKey.date,
      fieldName: AppEventTableColKey.date,
      name: AppEventTableColName.date,
      minWidth: 200,
      styles: columnStyles(true),
      onRender: (item: AppEvent) => <>{!isLoading ? item.appDate : <SkeletonLoader width="65%" height="100%" />}</>,
      onRenderHeader: () => renderHeader(AppEventTableColName.date, AppEventTableColKey.date),
      isSorted: sortColumn === AppEventTableColKey.date,
      isSortedDescending: sortColumn === AppEventTableColKey.date && isSortedDescending,
      onColumnClick: handleColumnClick,
    },
    {
      key: AppEventTableColKey.time,
      fieldName: AppEventTableColKey.time,
      name: AppEventTableColName.time,
      minWidth: 200,
      onRender: (item: AppEvent) => <>{!isLoading ? item.appTime : <SkeletonLoader width="65%" height="100%" />}</>,
      isSorted: sortColumn === AppEventTableColKey.time,
      isSortedDescending: sortColumn === AppEventTableColKey.time && isSortedDescending,
      onColumnClick: handleColumnClick,
      styles: columnStyles(true),
    },
  ];

  return (
    <div style={tableStyle(theme)}>
      {!isLoading && appEventData.length === 0 ? (
        <MessageBar messageBarType={MessageBarType.warning}>
          {'No data available for the selected date range'}
        </MessageBar>
      ) : (
        <DetailsList
          columns={columns}
          styles={customTableStyles(theme, '357')}
          items={isLoading ? dummyList : groupedItems}
          groups={isLoading ? undefined : groups}
        />
      )}
    </div>
  );
}
